import React from 'react'
import { useQuery } from '@tanstack/react-query'

import { Grid, Typography } from '@the-platform-group/component-library'
import { Button } from '@the-platform-group/component-library/Button'

import Spinner from 'components/Spinner'
import CustomerAuthorizationModal from 'components/CustomerModals/components/CustomerAuthorizationModal'
import UserNotifications from './components/UserNotifications'
import UserCustomers from './components/UserCustomers'
import UserIdentities from './components/UserIdentities'
import FactsetUsers from 'pages/Factset/FactsetUsers'

import { getUser } from 'services/users'
import { useSessionState } from 'contexts/Session'
import useCustomerAuthModal from './hooks/useCustomerAuthModal'
import { FactsetMetadataProvider } from 'contexts/FactsetMetadataContext'
import useUserForm from 'components/UserForm/useUserForm'
import useSyncUser from 'pages/Users/hooks/useSyncUser'
import { isAdmin } from 'utils/auth'
import { useStandaloneProvisioning } from 'contexts/StandaloneProvisioningContext'

export const USER_QUERY_KEY = 'getUser'

function UserDetailComponent() {
  const { currentUser } = useSessionState()
  const { userId, handleOpen, handleClose, open, onSave, isSaving } = useCustomerAuthModal()
  const { data: user, isLoading } = useQuery([USER_QUERY_KEY, userId], () => getUser(userId), {
    enabled: !!userId,
    refetchOnWindowFocus: false,
    useErrorBoundary: true,
    // Disable structural sharing to ensure fresh data is used when users are edited
    // React Query's structural sharing was preventing updated user data from showing
    // after invalidateQueries() because it considered the user list "structurally similar"
    structuralSharing: false,
  })

  const { handleUserEdit, getUserFormModal } = useUserForm()
  const { syncUser, isSyncing } = useSyncUser()
  const isStandaloneProvisioning = useStandaloneProvisioning()

  return isLoading || !user ? (
    <Spinner />
  ) : (
    <FactsetMetadataProvider>
      <Grid container justifyContent="space-between" alignItems="flex-end">
        <Grid item xs={6}>
          <Typography variant="h4" component="h1" gutterBottom>
            {[user.firstName, user.lastName].join(' ')}
          </Typography>
          <Typography variant="subtitle2" component="h1">
            {user.email}
          </Typography>
          <Typography variant="subtitle2" component="h3">
            Role: {user.role}
          </Typography>
        </Grid>
        <Grid item container justifyContent="flex-end" alignItems="center" spacing={1} xs="auto">
          {isAdmin(currentUser) ? (
            <>
              <Grid item>
                <Button onClick={() => handleUserEdit(user)}>Edit</Button>
              </Grid>
              <Grid item>
                <Button onClick={handleOpen}>Add Customer to User</Button>
              </Grid>
              {isStandaloneProvisioning && (
                <Grid item>
                  <Button
                    isLoading={isSyncing}
                    onClick={() =>
                      syncUser(user.userFdsadminDetails?.map(detail => detail.usernameSerial))
                    }
                  >
                    Sync User
                  </Button>
                </Grid>
              )}

              {getUserFormModal()}
              <CustomerAuthorizationModal
                isOpen={open}
                onClose={handleClose}
                onSave={onSave}
                isSaving={isSaving}
              />
            </>
          ) : null}
        </Grid>
      </Grid>
      <br />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <UserCustomers user={user} />
        </Grid>
      </Grid>
      <br />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <UserIdentities user={user} />
        </Grid>
      </Grid>
      <br />
      {!isStandaloneProvisioning && <FactsetUsers irwinUser={user} />}
      <br />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <UserNotifications user={user} />
        </Grid>
      </Grid>
      <br />
    </FactsetMetadataProvider>
  )
}

export default UserDetailComponent
